import { styled } from '@mui/system';
import FONTS_FAMILY from '../../../theme/FontsFamily';

// Transient props (prefixed with $) are style-only and must never reach the DOM.
const forwardProp = { shouldForwardProp: (prop) => !prop.startsWith('$') };

// Paleta institucional UCTT
export const INK = '#0F2350';
export const INK_SOFT = '#57697F';
export const INK_FAINT = '#8A99AC';
export const ACCENT = '#0F2350';
export const ACCENT_HOVER = '#EA361A';
export const FOCUS_RING = '#0063bc';
export const SURFACE_TINT = '#F1F6F7';
export const SURFACE_HOVER = '#F8FAFC';
export const ROW_ALT = '#F8FAFC';
export const BORDER = '#E1E9EC';
export const BORDER_STRONG = '#CFDCE0';
export const PANEL_SHADOW = '0 6px 20px rgba(15, 35, 80, 0.05)';

export const Container = styled('div')({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 0 40px 0'
});

export const HeaderSection = styled('div')({
    marginBottom: '32px',
    textAlign: 'left'
});

// Breadcrumb Styles
export const BreadcrumbWrapper = styled('div')({
    backgroundColor: 'transparent',
    width: '100%',
    marginBottom: '24px',
});

export const BreadcrumbInner = styled('div')({
    display: 'flex',
    alignItems: 'center',
});

export const BreadcrumbContent = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: FONTS_FAMILY.regular.body,
    fontSize: '13.5px',
    fontWeight: 600,
    color: '#8A99AC',
    textTransform: 'none',
});

export const BreadcrumbLink = styled('span')({
    cursor: 'pointer',
    color: '#57697F',
    transition: 'color 0.2s',
    '&:hover': {
        color: '#0063bc',
        textDecoration: 'underline',
    }
});

export const BreadcrumbSeparator = styled('span')({
    margin: '0 2px',
    color: '#CFDCE0',
    fontWeight: 400,
});

export const BreadcrumbCurrent = styled('span')({
    color: '#0F2350',
    fontWeight: 700,
});

/* Toolbar: search + last-updated indicator */
export const ToolbarSection = styled('section')({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '20px',
    flexWrap: 'wrap',
    '@media (max-width: 768px)': {
        flexDirection: 'column',
        alignItems: 'stretch',
    }
});

export const SearchBox = styled('div')({
    position: 'relative',
    flex: '1 1 300px',
    minWidth: '240px',
    display: 'flex',
    alignItems: 'center',
});

export const SearchIconWrap = styled('div')({
    position: 'absolute',
    left: '16px',
    display: 'flex',
    alignItems: 'center',
    color: INK_FAINT,
    pointerEvents: 'none',
});

export const SearchInput = styled('input')({
    width: '100%',
    height: '48px',
    boxSizing: 'border-box',
    border: `1.5px solid ${BORDER_STRONG}`,
    borderRadius: '14px',
    background: '#ffffff',
    padding: '0 16px 0 46px',
    fontFamily: FONTS_FAMILY.regular.body,
    fontSize: '14.5px',
    color: INK,
    outline: 'none',
    transition: 'all 0.2s ease',
    '&::placeholder': {
        color: INK_FAINT,
    },
    '&:focus': {
        borderColor: FOCUS_RING,
        boxShadow: `0 0 0 4px rgba(0, 99, 188, 0.12)`,
    },
    'html.dark &': {
        background: '#000000',
        borderColor: '#222222',
    }
});

export const UpdatedBadge = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: FONTS_FAMILY.bold.body,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: INK_SOFT,
    backgroundColor: '#FFFFFF',
    border: `1px solid ${BORDER}`,
    padding: '8px 16px',
    borderRadius: '999px',
    whiteSpace: 'nowrap',
    'html.dark &': {
        backgroundColor: '#000000',
        borderColor: '#222222',
    }
});

export const UpdatedDot = styled('span')({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#12A3BC',
    boxShadow: '0 0 0 3px rgba(18, 163, 188, 0.2)',
    flexShrink: 0,
});

/* Category filter chips */
export const ChipsRow = styled('div')({
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    marginBottom: '24px',
});

export const Chip = styled('button', forwardProp)(({ $active }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    height: '38px',
    padding: '0 18px',
    borderRadius: '999px',
    cursor: 'pointer',
    border: `1.5px solid ${$active ? INK : BORDER_STRONG}`,
    backgroundColor: $active ? INK : '#ffffff',
    color: $active ? '#ffffff' : INK_SOFT,
    fontSize: '13.5px',
    fontWeight: 700,
    transition: 'all 0.2s ease',
    boxShadow: $active ? '0 4px 12px rgba(15, 35, 80, 0.15)' : 'none',
    '&:hover': {
        borderColor: $active ? INK : ACCENT_HOVER,
        color: $active ? '#ffffff' : ACCENT_HOVER,
        backgroundColor: $active ? INK : '#ffffff',
        transform: 'translateY(-1px)'
    },
    '&:focus-visible': {
        outline: `2px solid ${FOCUS_RING}`,
        outlineOffset: '2px',
    },
    'html.dark &': {
        borderColor: $active ? '#7fb8e8' : '#333333',
        backgroundColor: $active ? 'rgba(0, 99, 188, 0.25)' : '#000000',
        color: '#ffffff',
    },
    'html.dark &:hover': {
        backgroundColor: $active ? 'rgba(0, 99, 188, 0.25)' : '#000000',
        borderColor: $active ? '#7fb8e8' : ACCENT_HOVER,
        color: $active ? '#ffffff' : ACCENT_HOVER,
    }
}));

export const ChipSwatch = styled('span', forwardProp)(({ $color }) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: $color || INK_FAINT,
    flexShrink: 0,
}));

/* Panel + document list */
export const Panel = styled('div')({
    backgroundColor: '#ffffff',
    border: `1px solid ${BORDER}`,
    borderRadius: '18px',
    boxShadow: PANEL_SHADOW,
    overflow: 'hidden',
    marginBottom: '32px',
    'html.dark &': {
        backgroundColor: '#000000',
        borderColor: '#222222',
    }
});

export const CountBar = styled('div')({
    padding: '16px 24px',
    borderBottom: `1px solid ${BORDER}`,
    fontFamily: FONTS_FAMILY.regular.body,
    fontSize: '13.5px',
    color: INK_SOFT,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    backgroundColor: '#FAFCFD',
    '& b': {
        color: INK,
        fontWeight: 700,
    },
    'html.dark &': {
        backgroundColor: '#0a0a0a',
        borderColor: '#222222',
    }
});

export const DocList = styled('ul')({
    listStyle: 'none',
    margin: 0,
    padding: 0,
});

export const DocItem = styled('li', forwardProp)(({ $edge }) => ({
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '50px 1fr auto',
    gap: '20px',
    alignItems: 'center',
    padding: '22px 26px',
    borderBottom: `1px solid ${BORDER}`,
    transition: 'all 0.2s ease',
    '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        backgroundColor: $edge || '#12A3BC',
        borderTopLeftRadius: '2px',
        borderBottomLeftRadius: '2px',
    },
    '&:last-child': {
        borderBottom: 'none',
    },
    '&:hover': {
        backgroundColor: '#F8FAFC',
    },
    '&:hover .doc-icon-glyph': {
        backgroundColor: INK,
        color: '#ffffff',
        borderColor: INK,
    },
    '@media (max-width: 680px)': {
        gridTemplateColumns: '42px 1fr',
        gap: '14px',
        padding: '18px 18px',
    },
    'html.dark &': {
        borderColor: '#222222',
    },
    'html.dark &:hover': {
        backgroundColor: '#0a0a0a',
    },
    'html.dark &:hover .doc-icon-glyph': {
        backgroundColor: '#ffffff',
        color: '#000000',
        borderColor: '#ffffff',
    }
}));

export const DocIcon = styled('div', forwardProp)(({ $ink, $bg }) => ({
    width: '50px',
    height: '50px',
    borderRadius: '12px',
    backgroundColor: $bg || '#E3F4F6',
    border: '1px solid rgba(0, 99, 188, 0.1)',
    color: $ink || '#0063bc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.25s ease',
    '@media (max-width: 680px)': {
        width: '42px',
        height: '42px',
    },
    'html.dark &': {
        backgroundColor: $bg ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 99, 188, 0.15)',
        borderColor: 'rgba(255, 255, 255, 0.12)',
        color: '#ffffff',
    }
}));

export const DocBody = styled('div')({
    minWidth: 0,
});

export const DocEyebrow = styled('div', forwardProp)(({ $ink }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    fontFamily: FONTS_FAMILY.bold.body,
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: $ink || '#0B8296',
    marginBottom: '6px',
    'html.dark &': {
        color: '#7fb8e8',
    }
}));

export const DocTitle = styled('p')({
    fontFamily: FONTS_FAMILY.bold.body,
    fontSize: '16.5px',
    fontWeight: 700,
    lineHeight: 1.4,
    color: INK,
    margin: '0 0 8px',
});

export const DocMeta = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
    fontFamily: FONTS_FAMILY.regular.body,
    fontSize: '12px',
    color: '#8A99AC',
    fontWeight: 500,
});

export const MetaItem = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
});

export const FormatTag = styled('span')({
    fontFamily: FONTS_FAMILY.bold.body,
    fontWeight: 800,
    color: INK,
    backgroundColor: '#E7ECF6',
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '10px',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    'html.dark &': {
        backgroundColor: 'rgba(0, 99, 188, 0.15)',
        color: '#7fb8e8',
    }
});

export const DocAside = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '12px',
    flexShrink: 0,
    '@media (max-width: 680px)': {
        gridColumn: '1 / -1',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        marginTop: '6px',
        paddingLeft: '56px',
    },
    '@media (max-width: 420px)': {
        flexDirection: 'column',
        alignItems: 'stretch',
        paddingLeft: '0',
    }
});

export const CategoryTag = styled('span', forwardProp)(({ $ink, $bg }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    height: '24px',
    padding: '0 12px',
    borderRadius: '999px',
    fontFamily: FONTS_FAMILY.bold.body,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    backgroundColor: $bg || '#E3F4F6',
    color: $ink || '#0F2350',
    whiteSpace: 'nowrap',
}));

export const ActionsRow = styled('div')({
    display: 'flex',
    gap: '10px',
    '@media (max-width: 420px)': {
        width: '100%',
        '& > *': { flex: 1 },
    }
});

export const EmptyState = styled('div')({
    padding: '64px 22px',
    textAlign: 'center',
    color: INK_FAINT,
});

export const PaginationBar = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
    padding: '18px 24px',
    borderTop: `1px solid ${BORDER}`,
    backgroundColor: '#FAFCFD',
    '@media (max-width: 640px)': {
        flexDirection: 'column',
        alignItems: 'stretch',
    },
    'html.dark &': {
        backgroundColor: '#0a0a0a',
        borderColor: '#222222',
    }
});

export const RowsPerPageGroup = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    flexWrap: 'wrap',
});

export const RowsPerPageLabel = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: FONTS_FAMILY.bold.body,
    fontSize: '11.5px',
    color: INK_SOFT,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontWeight: 700,
});

export const RowsPerPageSelect = styled('select')({
    background: 'transparent',
    border: 'none',
    borderBottom: `1.5px solid ${BORDER_STRONG}`,
    color: INK,
    fontFamily: FONTS_FAMILY.bold.body,
    fontWeight: 700,
    fontSize: '14px',
    cursor: 'pointer',
    outline: 'none',
    padding: '0 4px',
});

export const ResultsCount = styled('span')({
    fontFamily: FONTS_FAMILY.regular.body,
    fontSize: '13.5px',
    color: INK_SOFT,
    fontWeight: 500,
    '& b': {
        color: INK,
        fontWeight: 700,
    }
});

export const PagesGroup = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
});
